<template>
  <div class="home">
    <b-container>
      <h2>Organization Enrollment</h2>
      <div class="mt-3 mb-4">
        <p>Follow the prompts below to enroll your organization/facility in VaxCheck.
        </p>
      </div>
      <b-form @submit.stop.prevent="submitForm">
        <div class="card">
          <div class="card-header">
            <strong>{{ stepName.at(stepNumber) }}</strong>
          </div>
          <div class="card-body">
            <div v-if="stepNumber === 0">
              <b-row>
                <b-col cols="12" lg="6">
                  <BaseTextInput
                    setId="businessName"
                    v-model="reqAcc.businessName"
                    :v="$v.reqAcc.businessName"
                    slug="businessName"
                    label="Organization name"
                  />
                </b-col>
                <b-col cols="12" lg="6">
                  <label for="consentType">Consent type</label>
                  <b-form-select
                    id="consentType"
                    v-model="consentType"
                    :v="$v.reqAcc.consentType"
                    :state="null"
                    :options="opts.consentType"
                  />
                </b-col>
              </b-row>
              <b-row v-if="consentType">
                <b-col cols="12" lg="6">
                  <label for="intendedUse">Intended use</label>
                  <b-form-select
                    id="intendedUse"
                    v-model="reqAcc.intendedUse"
                    :v="$v.reqAcc.intendedUse"
                    :state="null"
                    :options="opts.intendedUse[consentType.toLowerCase()]"
                  />
                </b-col>
                <b-col cols="12" lg="6" v-if="reqAcc.intendedUse === 'Long-term care facility' && consentType !== 'Children'">
                  <label for="typeOfLtcf">Type of Long-term care Facility</label>
                  <b-form-select
                    id="typeOfLtcf"
                    v-model="reqAcc.typeOfLtcf"
                    :v="$v.reqAcc.typeOfLtcf"
                    :state="null"
                    :options="opts.typeOfLtcf"
                  />
                </b-col>
                <b-col cols="12" lg="6" v-if="reqAcc.intendedUse === 'Certified child care center' && consentType === 'Children'">
                  <BaseTextInput
                    id="dhsCertificationNumber"
                    v-model="reqAcc.dhsCertificationNumber"
                    :v="$v.reqAcc.dhsCertificationNumber"
                    slug="dhsCertificationNumber"
                    label="DCYF Certification Number"
                  />
                </b-col>
              </b-row>
            </div>
            <div v-if="stepNumber === 1">
              <b-row>
                <b-col>
                  <BaseTextInput
                    setId="facilityName"
                    v-model="reqAcc.facilityName"
                    :v="$v.reqAcc.facilityName"
                    slug="facilityName"
                    label="Facility name"
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <BaseTextInput
                    setId="address"
                    v-model="reqAcc.address"
                    :v="$v.reqAcc.address"
                    slug="address"
                    label="Address"
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" lg="5">
                  <BaseTextInput
                    setId="city"
                    v-model="reqAcc.city"
                    :v="$v.reqAcc.city"
                    slug="city"
                    label="City"
                  />
                </b-col>
                <b-col cols="12" lg="4">
                  <BaseSelector
                    setId="state"
                    v-model="reqAcc.state"
                    :v="$v.reqAcc.state"
                    slug="state"
                    label="State"
                    :options=opts.stateOpts
                  />
                </b-col>
                <b-col cols="12" lg="3">
                  <BaseTextInput
                    setId="zipCode"
                    v-model="reqAcc.zipCode"
                    :v="$v.reqAcc.zipCode"
                    slug="zipCode"
                    label="Zip code"
                  />
                </b-col>
              </b-row>
            </div>
            <div v-if="stepNumber === 2">
              <b-row>
                <b-col cols="12" lg="6">
                  <BaseTextInput
                    setId="firstName"
                    v-model="reqAcc.firstName"
                    :v="$v.reqAcc.firstName"
                    slug="firstName"
                    label="First name"
                  />
                </b-col>
                <b-col cols="12" lg="6">
                  <BaseTextInput
                    setId="lastName"
                    v-model="reqAcc.lastName"
                    :v="$v.reqAcc.lastName"
                    slug="lastName"
                    label="Last name"
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" lg="6">
                  <BaseTextInput
                    setId="jobTitle"
                    v-model="reqAcc.jobTitle"
                    :v="$v.reqAcc.jobTitle"
                    slug="jobTitle"
                    label="Job title"
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" lg="6">
                  <BaseTextInput
                    setId="emailAddress"
                    v-model="reqAcc.emailAddress"
                    :v="$v.reqAcc.emailAddress"
                    slug="emailAddress"
                    label="Email address"
                  />
                </b-col>
                <b-col cols="12" lg="6">
                  <BaseTextInput
                    setId="phoneNumber"
                    v-model="reqAcc.phoneNumber"
                    :v="$v.reqAcc.phoneNumber"
                    slug="phoneNumber"
                    label="Phone number"
                  />
                </b-col>
              </b-row>
            </div>
            <b-row cols="2">
              <b-col offset="3" class="navigation">
                <b-button
                  class="bg-mdh"
                  role="button"
                  @click="backButtonHandler"
                >
                  &lt; Back to previous page
                </b-button>
                <b-button
                  class="bg-mdh"
                  role="button"
                  type="submit"
                  v-if="stepNumber === 2"
                >
                  Submit
                </b-button>
                <b-button
                  class="bg-mdh"
                  role="button"
                  :disabled="stepNumber === stepName.length - 1"
                  @click="stepNumber++"
                  v-if="stepNumber!==2"
                >
                  Proceed to next page &gt;
                </b-button>
              </b-col>
              <b-col cols="12" lg="3"/>
            </b-row>
          </div>
        </div>
      </b-form>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios'
import {email, maxLength, required, requiredIf} from 'vuelidate/lib/validators'
import {commonOptions} from '@/mixins/commonOptions.js'
import BaseTextInput from "@/components/templates/input/BaseTextInput.vue";
import BaseSelector from "@/components/templates/input/BaseSelector.vue";
import router from "@/router";

export default {
  components: {BaseSelector, BaseTextInput},
  mixins: {commonOptions},

  data: () => ({
    reqAcc: {
      businessName: '',
      intendedUse: '',
      typeOfLtcf: '',
      facilityName: '',
      address: '',
      city: '',
      state: 'MN',
      zipCode: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      emailAddress: '',
      phoneNumber: '',
      dhsCertificationNumber: ''
    },
    consentType: '',
    opts: {
      stateOpts: commonOptions.data().opts.stateOptions,
      consentType: [
        'Employees',
        'Children'
      ],
      intendedUse: {
        employees: [
          'Long-term care facility',
          'Hospital',
          'Non-health care organization',
          'Other health care facility'
        ],
        children: [
          'Certified child care center'
        ]
      },
      typeOfLtcf: [
        'Federally certified Skilled Nursing Facility',
        'Non-federally certified Skilled Nursing Facility',
        'Assisted living facility',
        'Combination',
        'Other long-term care facility'
      ]
    },
    stepNumber: 0,
    stepName: [
      'Intended use',
      'Facility information',
      'Primary contact information'
    ]
  }),

  validations() {
    return {
      reqAcc: {
        businessName: {required, maxLength: maxLength(75)},
        intendedUse: {required},
        typeOfLtcf: {
          required: requiredIf(() => {
            return this.reqAcc.intendedUse === 'Long-term care facility'
          })
        },
        facilityName: {required, maxLength: maxLength(75)},
        address: {required, maxLength: maxLength(75)},
        city: {required, maxLength: maxLength(75)},
        state: {required, maxLength: maxLength(2)},
        zipCode: {required, maxLength: maxLength(10)},
        firstName: {required, maxLength: maxLength(75)},
        lastName: {required, maxLength: maxLength(75)},
        jobTitle: {required, maxLength: maxLength(75)},
        emailAddress: {required, email, maxLength: maxLength(100)},
        phoneNumber: {required, maxLength: maxLength(15)},
        dhsCertificationNumber: {maxLength: maxLength(15),
          required: requiredIf(() => {
            return this.reqAcc.intendedUse === 'Certified child care center'
          })
        }
      },
      consentType: {required}
    }
  },

  watch: {
    'reqAcc.emailAddress'(email) {
      this.reqAcc.emailAddress = email.toLowerCase().trim()
    }
  },


  methods: {
    submitForm() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/user/isDuplicate/${this.reqAcc.emailAddress.toLowerCase().trim()}`)
          .then(response => {
            if (response.data === false) {
              axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/submit`, this.reqAcc)
                .then(response => {
                  if (response.status === 200) {
                    this.showRootToast('Success', 'Access request submitted.', 'success')
                    router.push('/thanks')
                  } else {
                    this.showRootToast('Error', 'Error submitting request.', 'danger')
                  }
                })
            } else {
              this.showRootToast('Error', 'An account is already registered with this email.', 'danger')
            }
          }).catch((error) => {
            this.showRootToast('Error', 'Error submitting request.', 'danger')
        })
      }
    },
    backButtonHandler() {
      if (this.stepNumber > 0) {
        this.stepNumber--
      } else if (this.stepNumber === 0) {
        router.push('/')
      } else {
        this.$emit('clicked', 'isEnrolled')
      }
    },
    showRootToast(title, message, type) {
      this.$root.$bvToast.toast(message, {
        title: title,
        toaster: 'b-toaster-top-center',
        solid: true,
        variant: type,
        appendToast: true
      })
    },
  },
}
</script>

<style>
.row {
  margin-bottom: 1.5em;
}

.navigation {
  display: flex;
  flex-direction: row;
  justify-content: center;
}
</style>
